import type { FC, ReactNode } from 'react';
import { WikiLink as Link } from './WikiLink';
import { DesktopFullBleed } from '../../../contexts/DesktopContentWidthContext';
import { RiArrowRightSLine } from '../../../components/icons';
import { cn } from '../../../utils/cn';
import { wikiProse } from '../wikiTypography';
import type { WikiT } from '../wikiText';
import type { WikiCrumb } from '../wikiCrumbs';

export const WikiContainer: FC<{ children: ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cn('mx-auto flex w-full max-w-[1200px] flex-col gap-8 px-4 pb-12 pt-3 lg:gap-10 lg:px-6 lg:pt-6', className)}>
    <DesktopFullBleed />
    {children}
  </div>
);

// WHY: одна сетка на страницу — блоки либо во всю ширину, либо 7/5 с общими границами колонок
export const WIKI_PAGE_GRID = 'flex flex-col gap-8 lg:grid lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:items-start lg:gap-x-8 lg:gap-y-10';

export const WikiBreadcrumbs: FC<{ items: WikiCrumb[]; t: WikiT }> = ({ items, t }) => (
  <nav aria-label={t('breadcrumbs')} className="-mb-4 lg:-mb-6">
    <ol className="m-0 -my-[11px] flex list-none items-center gap-x-1 overflow-hidden px-0 py-[11px] text-[13px] leading-[18px] text-label-secondary">
      {items.map((item, i) => (
        <li key={`${item.label}-${i}`} className={cn('flex items-center gap-1', i === items.length - 1 ? 'min-w-0' : 'max-w-[45%] shrink-0')}>
          {i > 0 && <RiArrowRightSLine aria-hidden size={14} className="shrink-0 text-label-secondary rtl:-scale-x-100" />}
          {item.href ? (
            <Link href={item.href} className="-my-[11px] block truncate py-[11px] hover:text-label-primary">
              {item.label}
            </Link>
          ) : (
            <span aria-current="page" title={item.label} className="truncate text-label-primary">
              {item.label}
            </span>
          )}
        </li>
      ))}
    </ol>
  </nav>
);

const H1 = 'm-0 text-[28px] font-bold leading-[34px] tracking-[-0.4px] [overflow-wrap:anywhere] [text-wrap:balance] lg:text-[34px] lg:leading-[41px]';

export const WikiPageHeader: FC<{ title: string; count?: number; subtitle?: ReactNode; children?: ReactNode }> = ({ title, count, subtitle, children }) => (
  <header className="flex flex-col gap-2">
    {count === undefined ? (
      <h1 className={cn(H1, 'text-label-primary')}>{wikiProse(title)}</h1>
    ) : (
      <div className={cn(H1, 'text-label-primary')}>
        <h1 className="m-0 inline text-[length:inherit] font-[inherit] leading-[inherit]">{wikiProse(title)}</h1>
        {/* WHY: неразрывные пробелы держат счётчик на одной строке с последним словом заголовка */}
        <span className="font-semibold text-label-secondary">
          <span aria-hidden>{' · '}</span>
          {count}
        </span>
      </div>
    )}
    {subtitle && (
      <p className="m-0 text-[15px] leading-[22px] text-label-secondary [text-wrap:pretty]">
        {typeof subtitle === 'string' ? wikiProse(subtitle) : subtitle}
      </p>
    )}
    {children}
  </header>
);

interface WikiSectionProps {
  title: string;
  id?: string;
  action?: { label: string; href: string };
  hint?: string;
  children: ReactNode;
  className?: string;
}

export const WikiSection: FC<WikiSectionProps> = ({ title, id, action, hint, children, className }) => (
  <section aria-labelledby={id} className={cn('flex flex-col gap-3', className)}>
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <h2 id={id} className="m-0 min-w-0 scroll-mt-4 text-[22px] font-bold leading-[28px] text-label-primary [overflow-wrap:anywhere] [text-wrap:balance]">
        {wikiProse(title)}
      </h2>
      {action && (
        <Link href={action.href} className="inline-flex min-h-10 items-center gap-0.5 text-[15px] font-medium text-accent-blue hover:underline">
          {wikiProse(action.label)}
          <RiArrowRightSLine aria-hidden size={18} className="rtl:-scale-x-100" />
        </Link>
      )}
    </div>
    {hint && <p className="m-0 -mt-1 text-[14px] leading-[20px] text-label-secondary">{wikiProse(hint)}</p>}
    {children}
  </section>
);

export const WikiPanel: FC<{ children: ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cn('rounded-[22px] bg-bg-secondary p-4', className)}>{children}</div>
);
